import type { ComponentProps, ReactNode } from "react"
import { useEffect, useId, useRef, useState } from "react"
import { useSearch } from "@tanstack/react-router"
import {
	IconCircleCheckOutline18 as CircleCheck,
	IconCodeOutline18 as Code,
	IconEnvelopeOutline18 as Envelope,
	IconGlobe2Outline18 as Globe,
	IconHourglassOutline18 as Hourglass,
	IconSquaresConnectedOutline18 as SquaresConnected,
} from "nucleo-ui-outline-18"
import Balancer from "react-wrap-balancer"

import { MembershipRole } from "@productlane/db/browser"
import { cn } from "@productlane/lib/src/browser"
import {
	Badge,
	Button,
	Input,
	RadioGroup,
	RadioGroupItem,
} from "@productlane/ui"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"

import type { AppSearch } from "@/lib/search-params"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import {
	deriveName,
	invalidEmailMessage,
	partitionEmails,
} from "@/lib/invite-emails"
import { showInviteErrorToast } from "@/lib/invite-error"
import {
	clearPendingSiteImport,
	readPendingSiteImport,
} from "@/lib/pending-site-import"
import { useSession } from "@/lib/session"
import {
	API_REFERENCE_CREATED,
	NEW_API_REFERENCE,
} from "@/screens/app/helpCenter/api-reference-defaults"
import { normalizeSiteUrl } from "@/screens/settings/import-export/markdown-import/utils/site-url"

const ONBOARDING_PAGE_LIMIT = 1000

type Source = "code" | "api" | "link" | "connect"
type GithubReturn = "connected" | "pending" | "error" | null

// Input hardcodes a 1.75 stroke, so the override has to wrap the icon.
const ThinGlobe = (props: ComponentProps<typeof Globe>) => (
	<Globe {...props} strokeWidth={1} />
)
const ThinEnvelope = (props: ComponentProps<typeof Envelope>) => (
	<Envelope {...props} strokeWidth={1} />
)

/** The GitHub callback sends the installer back here with how it went. */
function readGithubReturn(search: AppSearch): GithubReturn {
	const status = search.github
	if (status === "connected" || status === "pending") return status
	if (search.github_error) return "error"
	return null
}

function SourceRow({
	value,
	selected,
	icon,
	title,
	badge,
	description,
	children,
}: {
	value: Source
	selected: boolean
	icon: ReactNode
	title: string
	badge?: ReactNode
	description: string
	children: ReactNode
}) {
	const id = useId()
	const bodyRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (selected) {
			bodyRef.current?.querySelector("input")?.focus({ preventScroll: true })
		}
	}, [selected])
	return (
		<div
			data-testid={`source-${value}`}
			data-selected={selected}
			className={cn(
				"rounded-xl ring-1",
				selected
					? "bg-white ring-gray-900 dark:bg-gray-850 dark:ring-gray-100"
					: "bg-black/[0.02] ring-black/5 hover:bg-black/[0.035] dark:bg-white/[0.02] dark:ring-white/10 dark:hover:bg-white/[0.04]",
			)}
		>
			<div className="relative flex items-center gap-3.5 px-4 py-3.5">
				<RadioGroupItem id={id} value={value} aria-label={title} />
				{/* Stretched over the row, so a hover or click anywhere reaches the radio.
				    The app has no preflight, so the pseudo-element needs its content set here. */}
				<label
					htmlFor={id}
					className="flex min-w-0 flex-1 cursor-default items-center gap-3.5 after:absolute after:inset-0 after:content-['']"
				>
					<span className="flex size-8 flex-none items-center justify-center rounded-lg bg-white ring-1 ring-black/[0.08] dark:bg-gray-800 dark:ring-white/10">
						{icon}
					</span>
					<span className="flex min-w-0 flex-1 flex-col gap-0.5">
						<span className="flex items-center gap-2">
							<span className="text-[0.9rem] font-medium">{title}</span>
							{badge}
						</span>
						<span className="text-[13px] text-tertiary">{description}</span>
					</span>
				</label>
			</div>
			{/* Every body stays mounted so opening and closing can both ease. */}
			<div
				data-testid={`source-${value}-body`}
				inert={!selected}
				className={cn(
					"grid transition-[grid-template-rows,opacity] duration-200 ease-out-quint motion-reduce:transition-none",
					selected
						? "grid-rows-[1fr] opacity-100"
						: "grid-rows-[0fr] opacity-0",
				)}
			>
				{/* Clips the reveal at the bottom only, so the inputs' outer rings survive. */}
				<div
					ref={bodyRef}
					className="min-h-0 [clip-path:inset(-4px_-4px_0_-4px)]"
				>
					<div className="flex items-start gap-2 pb-4 pl-[5.75rem] pr-4">
						{children}
					</div>
				</div>
			</div>
		</div>
	)
}

export function ConnectDocsSlide({ nextSlide }: { nextSlide: () => void }) {
	const { session } = useSession()
	const search = useSearch({ strict: false })
	const [githubReturn] = useState(() => readGithubReturn(search))
	const [pendingUrl] = useState(() => readPendingSiteImport())
	const [source, setSource] = useState<Source>(
		pendingUrl && !githubReturn ? "link" : "code",
	)
	const [url, setUrl] = useState(pendingUrl ?? "")
	const [urlError, setUrlError] = useState<string | null>(null)
	const [inviting, setInviting] = useState(false)
	const [devEmail, setDevEmail] = useState("")
	const [emailError, setEmailError] = useState<string | null>(null)
	const [mcpUrl, setMcpUrl] = useState("")
	const [mcpUrlError, setMcpUrlError] = useState<string | null>(null)
	const [specUrl, setSpecUrl] = useState("")
	const [specUrlError, setSpecUrlError] = useState<string | null>(null)

	const startImport = api.workspace.startHelpCenterSiteImport.useMutation()
	const createApiReference = api.apiReference.create.useMutation()
	const inviteMutation = api.workspace.inviteMembers.useMutation()
	const connectHelpCenter = api.customData.connectHelpCenter.useMutation()
	const installUrl = api.repoIntegration.getInstallUrl.useQuery(
		{ returnTo: "onboarding" },
		{ staleTime: Infinity, retry: false },
	)

	// A link can carry any site, so the hero's address is offered, never imported unasked.
	useEffect(() => {
		if (pendingUrl) clearPendingSiteImport()
	}, [pendingUrl])

	useEffect(() => {
		if (githubReturn !== "error") return
		toast.error({
			title: "GitHub didn't connect",
			description: "Try again, or invite the developer who owns the repo.",
			icon: GitHubIcon,
		})
	}, [githubReturn])

	const track = (event: string, extra?: Record<string, string>) => {
		try {
			analytics.track(event, {
				userId: session?.userId,
				workspaceId: session?.workspaceId,
				...extra,
			})
		} catch {
			// Never let analytics failures block onboarding progress.
		}
	}

	const connectGithub = () => {
		const target = installUrl.data?.url
		if (!target) {
			toast.error({
				title: "Couldn't open GitHub",
				description: installUrl.error?.message ?? "Try again in a moment.",
				icon: GitHubIcon,
			})
			return
		}
		track("onboarding-github-install-clicked")
		window.location.href = target
	}

	const sendInvite = async () => {
		const email = devEmail.trim()
		if (!email) {
			setEmailError("Enter your developer's email address.")
			return
		}
		if (partitionEmails([email]).invalid.length > 0) {
			setEmailError(invalidEmailMessage([email]))
			return
		}

		let results
		try {
			results = await inviteMutation.mutateAsync({
				invites: [{ email, name: deriveName(email) }],
				// The GitHub callback only accepts admins, so the developer needs the role to connect.
				role: MembershipRole.ADMIN,
			})
		} catch (error) {
			showInviteErrorToast(error as { message?: string })
			return
		}

		const failure = results.find((result) => !result.ok)
		if (failure) {
			setEmailError(failure.error ?? "Couldn't send the invite.")
			return
		}

		toast.success({ title: `Invite sent to ${email}`, icon: Envelope })
		track("onboarding-github-developer-invited")
		nextSlide()
	}

	const importSite = async () => {
		const target = normalizeSiteUrl(url)
		if (!target) {
			setUrlError("Enter the address of your help center.")
			return
		}

		try {
			await startImport.mutateAsync({
				url: target,
				limit: ONBOARDING_PAGE_LIMIT,
				publish: true,
			})
		} catch (cause) {
			setUrlError(
				cause instanceof Error
					? cause.message
					: "Couldn't read that help center. Try again.",
			)
			return
		}

		const fromHero =
			pendingUrl !== null && target === normalizeSiteUrl(pendingUrl)
		track("onboarding-docs-import-started", {
			source: fromHero ? "marketing-hero" : "onboarding",
		})
		toast.success({
			title: "Reading your help center",
			description: "We read it in the background. Keep going here.",
		})
		nextSlide()
	}

	const importSpec = async () => {
		const address = specUrl.trim()
		if (!address) {
			setSpecUrlError("Enter the address of your OpenAPI spec.")
			return
		}

		try {
			await createApiReference.mutateAsync({
				...NEW_API_REFERENCE,
				portalInstanceId: null,
				source: {
					type: "url",
					url: /^https?:\/\//i.test(address) ? address : `https://${address}`,
				},
			})
		} catch (cause) {
			setSpecUrlError(
				cause instanceof Error ? cause.message : "Couldn't read the spec",
			)
			return
		}

		track("onboarding-api-reference-created")
		toast.success({ title: API_REFERENCE_CREATED, icon: Code })
		nextSlide()
	}

	const connectExisting = async () => {
		if (!mcpUrl.trim()) {
			setMcpUrlError("Enter the address of your help center.")
			return
		}

		let connected
		try {
			connected = await connectHelpCenter.mutateAsync({ url: mcpUrl })
		} catch (cause) {
			setMcpUrlError(
				cause instanceof Error
					? cause.message
					: "Couldn't connect that help center. Try again.",
			)
			return
		}

		track("onboarding-help-center-connected", { url: connected.url })
		toast.success({
			title: "Help center connected",
			description: `Your agent now searches ${connected.title}.`,
			icon: SquaresConnected,
		})
		nextSlide()
	}

	const codeBody = (() => {
		if (githubReturn === "connected" || githubReturn === "pending") {
			const connected = githubReturn === "connected"
			return (
				<>
					<div className="flex h-9 min-w-0 flex-1 items-center gap-2 text-[13px] text-secondary">
						{connected ? (
							<CircleCheck className="size-4 flex-none text-green-500" />
						) : (
							<Hourglass className="size-4 flex-none text-tertiary" />
						)}
						<span className="truncate">
							{connected
								? "GitHub is connected."
								: "Waiting for your GitHub admin to approve."}
						</span>
					</div>
					<Button size="lg" onClick={nextSlide}>
						Continue
					</Button>
				</>
			)
		}

		if (inviting) {
			return (
				<>
					<Input
						name="developer-email"
						type="email"
						icon={ThinEnvelope}
						className="min-w-0 flex-1"
						inputClassName="h-9 rounded-lg"
						placeholder="dev@acme.com"
						aria-label="Developer email"
						value={devEmail}
						errorMessage={emailError}
						autoFocus
						onChange={(event) => {
							setDevEmail(event.target.value)
							setEmailError(null)
						}}
						onKeyDown={(event) => {
							if (event.key !== "Enter") return
							event.preventDefault()
							void sendInvite()
						}}
					/>
					<Button
						size="lg"
						isLoading={inviteMutation.isLoading}
						onClick={() => void sendInvite()}
					>
						Send invite
					</Button>
					<Button
						size="lg"
						variant="ghost"
						onClick={() => {
							setInviting(false)
							setEmailError(null)
						}}
					>
						Cancel
					</Button>
				</>
			)
		}

		return (
			<>
				<Button
					size="lg"
					Icon={GitHubIcon}
					isLoading={installUrl.isLoading}
					onClick={connectGithub}
				>
					Connect GitHub
				</Button>
				<Button
					size="lg"
					variant="outline"
					Icon={Envelope}
					onClick={() => setInviting(true)}
				>
					Invite a developer
				</Button>
			</>
		)
	})()

	return (
		<div className="flex flex-col items-center text-center">
			<div className="mb-1 text-3xl font-bold tracking-tight">
				Teach your agent
			</div>
			<Balancer className="tracking-tight text-secondary">
				Pick what your agent learns from first. You can add the others later.
			</Balancer>

			<RadioGroup
				value={source}
				onValueChange={(next) => setSource(next as Source)}
				aria-label="What your agent learns from"
				className="mt-7 flex w-full max-w-lg flex-col gap-2 text-left"
			>
				<SourceRow
					value="code"
					selected={source === "code"}
					icon={<GitHubIcon className="size-[18px] flex-none" />}
					title="Your codebase"
					badge={
						<Badge size="sm" variant="green">
							Recommended
						</Badge>
					}
					description="We read your repo and write an article for every screen."
				>
					{codeBody}
				</SourceRow>

				<SourceRow
					value="api"
					selected={source === "api"}
					icon={
						<Code
							strokeWidth={1.25}
							className="size-[18px] flex-none text-secondary"
						/>
					}
					title="OpenAPI spec"
					description="We write a page for every endpoint in your OpenAPI spec."
				>
					<Input
						name="openapi-spec-url"
						icon={ThinGlobe}
						className="min-w-0 flex-1"
						inputClassName="h-9 rounded-lg"
						placeholder="api.acme.com/openapi.json"
						aria-label="OpenAPI spec address"
						value={specUrl}
						errorMessage={specUrlError}
						onChange={(event) => {
							setSpecUrl(event.target.value)
							setSpecUrlError(null)
						}}
						onKeyDown={(event) => {
							if (event.key !== "Enter") return
							event.preventDefault()
							void importSpec()
						}}
					/>
					<Button
						size="lg"
						isLoading={createApiReference.isLoading}
						onClick={() => void importSpec()}
					>
						Import
					</Button>
				</SourceRow>

				<SourceRow
					value="link"
					selected={source === "link"}
					icon={
						<Globe
							strokeWidth={1.25}
							className="size-[18px] flex-none text-secondary"
						/>
					}
					title="Migrate help center"
					description="We import every page of your help center as an article."
				>
					<Input
						name="docs-url"
						icon={ThinGlobe}
						className="min-w-0 flex-1"
						inputClassName="h-9 rounded-lg"
						placeholder="help.acme.com"
						aria-label="Help center address"
						value={url}
						errorMessage={urlError}
						onChange={(event) => {
							setUrl(event.target.value)
							setUrlError(null)
						}}
						onKeyDown={(event) => {
							if (event.key !== "Enter") return
							event.preventDefault()
							void importSite()
						}}
					/>
					<Button
						size="lg"
						isLoading={startImport.isLoading}
						onClick={() => void importSite()}
					>
						Import
					</Button>
				</SourceRow>

				<SourceRow
					value="connect"
					selected={source === "connect"}
					icon={
						<SquaresConnected
							strokeWidth={1.25}
							className="size-[18px] flex-none text-secondary"
						/>
					}
					title="Existing help center"
					description="Your agent searches your Mintlify or GitBook docs."
				>
					<Input
						name="help-center-mcp-url"
						icon={ThinGlobe}
						className="min-w-0 flex-1"
						inputClassName="h-9 rounded-lg"
						placeholder="docs.acme.com"
						aria-label="Help center or MCP server address"
						value={mcpUrl}
						errorMessage={mcpUrlError}
						onChange={(event) => {
							setMcpUrl(event.target.value)
							setMcpUrlError(null)
						}}
						onKeyDown={(event) => {
							if (event.key !== "Enter") return
							event.preventDefault()
							void connectExisting()
						}}
					/>
					<Button
						size="lg"
						isLoading={connectHelpCenter.isLoading}
						onClick={() => void connectExisting()}
					>
						Connect
					</Button>
				</SourceRow>
			</RadioGroup>

			<Button
				variant="link"
				className="mt-5 w-full max-w-xxs opacity-70 hover:opacity-80"
				onClick={() => {
					analytics.track("skip-connect-docs-onboarding")
					nextSlide()
				}}
			>
				{"I'll do this later"}
			</Button>
		</div>
	)
}
